import {useCallback, useEffect, useRef, useState} from 'react';
import {SLIDE_DURATION_MS, TRANSITION_COMPLETE_TIMEOUT_MS} from './constants/pageTransitionTiming';
import {NAVIGATION_DIRECTION, type NavigationDirection} from './navigationDirection';
import {resolveTransitionBehavior} from './policies/resolveTransitionBehavior';
import {TRANSITION_BEHAVIOR_TYPE} from './policies/transitionBehavior.types';

const TRANSLATE_ZERO = 'translate3d(0, 0, 0)';
const TRANSLATE_LEFT = 'translate3d(-100%, 0, 0)';
const TRANSLATE_RIGHT = 'translate3d(100%, 0, 0)';

export enum PaneSlot {
    Active = 'active',
    Inactive = 'inactive',
}

export type PaneState = {
    outlet: React.ReactNode;
    transform: string;
    transition: string;
    zIndex: number;
    visible: boolean;
    shouldCompleteOnTransitionEnd: boolean;
};

const getInactiveSlot = (slot: PaneSlot) => (slot === PaneSlot.Active ? PaneSlot.Inactive : PaneSlot.Active);

export const usePageTransition = (initialView: React.ReactNode) => {
    const keyRef = useRef(0);
    const pendingLocationKeyRef = useRef(0);
    const runtimeRef = useRef({
        timeoutId: null as ReturnType<typeof setTimeout> | null,
        rafId1: null as number | null,
        rafId2: null as number | null,
        lastCommittedLocationKey: 0,
        activeSlot: PaneSlot.Active,
    });

    const [paneOutlets, setPaneOutlets] = useState<Record<PaneSlot, React.ReactNode>>({
        [PaneSlot.Active]: initialView,
        [PaneSlot.Inactive]: null,
    });
    const [uiState, setUiState] = useState({
        activeSlot: PaneSlot.Active,
        isTransitionRunning: false,
        direction: NAVIGATION_DIRECTION.FORWARD as NavigationDirection,
        isDualPaneVisible: false,
    });

    const runtime = runtimeRef.current;

    const clearPending = () => {
        if (runtime.timeoutId) {
            clearTimeout(runtime.timeoutId);
            runtime.timeoutId = null;
        }
        if (runtime.rafId1 !== null) {
            cancelAnimationFrame(runtime.rafId1);
            runtime.rafId1 = null;
        }
        if (runtime.rafId2 !== null) {
            cancelAnimationFrame(runtime.rafId2);
            runtime.rafId2 = null;
        }
    };

    useEffect(() => clearPending, []);

    const finishTransition = useCallback(() => {
        clearPending();
        const inactiveSlot = getInactiveSlot(runtime.activeSlot);

        setUiState((prev) => ({...prev, isTransitionRunning: false, isDualPaneVisible: false}));
        setPaneOutlets((prev) => ({...prev, [inactiveSlot]: null}));
        runtime.lastCommittedLocationKey = pendingLocationKeyRef.current;
    }, []);

    const navigate = useCallback(
        (nextView: React.ReactNode, navDirection: NavigationDirection) => {
            const capturedKey = ++keyRef.current;
            pendingLocationKeyRef.current = capturedKey;

            const transitionBehavior = resolveTransitionBehavior({
                incomingKey: capturedKey,
                lastCommittedLocationKey: runtime.lastCommittedLocationKey,
                explicitDirection: navDirection,
            });

            if (transitionBehavior.type === TRANSITION_BEHAVIOR_TYPE.IGNORE) return;

            const nextSlot = getInactiveSlot(runtime.activeSlot);
            runtime.activeSlot = nextSlot;

            setUiState((prev) => ({
                ...prev,
                activeSlot: nextSlot,
                direction: transitionBehavior.direction,
                isDualPaneVisible: true,
            }));

            clearPending();

            runtime.rafId1 = requestAnimationFrame(() => {
                runtime.rafId2 = requestAnimationFrame(() => {
                    setPaneOutlets((prev) => ({...prev, [nextSlot]: nextView}));
                    setUiState((prev) => ({...prev, isTransitionRunning: true}));
                });
            });

            runtime.timeoutId = setTimeout(() => {
                finishTransition();
            }, TRANSITION_COMPLETE_TIMEOUT_MS);
        },
        [finishTransition]
    );

    const {activeSlot, isTransitionRunning, direction: currentDirection, isDualPaneVisible} = uiState;
    const isForward = currentDirection === NAVIGATION_DIRECTION.FORWARD;
    const transitionStyle = isTransitionRunning ? `transform ${SLIDE_DURATION_MS}ms ease-in-out` : 'none';

    const getTranslate = (isFront: boolean): string => {
        if (!isDualPaneVisible) return TRANSLATE_ZERO;
        if (isFront) return isTransitionRunning ? TRANSLATE_ZERO : isForward ? TRANSLATE_RIGHT : TRANSLATE_LEFT;
        return isTransitionRunning ? (isForward ? TRANSLATE_LEFT : TRANSLATE_RIGHT) : TRANSLATE_ZERO;
    };

    const buildPaneState = (slot: PaneSlot): PaneState => {
        const isFront = slot === activeSlot;

        return {
            outlet: paneOutlets[slot],
            transform: getTranslate(isFront),
            transition: transitionStyle,
            zIndex: isFront ? 1 : 0,
            visible: isDualPaneVisible || isFront,
            shouldCompleteOnTransitionEnd: isFront,
        };
    };

    return {
        foregroundPane: buildPaneState(PaneSlot.Active),
        backgroundPane: buildPaneState(PaneSlot.Inactive),
        isAnimating: isDualPaneVisible,
        finishTransition,
        navigate,
    };
};
